/**
 * Modal that shows the translated privacy notice text.
 */
import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { useConfigurations } from "../../../context/ConfigurationsContext/ConfigurationsContext";

type PrivacyLocale = "en" | "es" | "pt" | "fr" | "de" | "it" | "zh";

type PrivacyTranslation = {
  title: string;
  close: string;
  content: string;
};

type ModalTextPrivacityProps = {
  open?: boolean;
  onClose?: () => void;
  text?: string;
  title?: string;
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  zIndex: 2000,
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const normalizeLanguageName = (languageName = "") => {
  try {
    return languageName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  } catch {
    return languageName.toLowerCase();
  }
};

const resolvePrivacyLocale = (
  languageName?: string,
  flag?: string,
): PrivacyLocale => {
  const name = normalizeLanguageName(languageName);
  const flagUp = (flag ?? "").toUpperCase();

  if (
    name.includes("espanol") ||
    name.includes("spanish") ||
    ["MX", "ES", "AR", "CO", "CL", "PE"].includes(flagUp)
  ) {
    return "es";
  }

  if (
    name.includes("portugues") ||
    name.includes("portuguese") ||
    name === "pt" ||
    name.startsWith("pt-") ||
    ["BR", "PT"].includes(flagUp)
  ) {
    return "pt";
  }

  if (
    name.includes("francais") ||
    name.includes("french") ||
    name === "fr" ||
    name.startsWith("fr-") ||
    flagUp === "FR"
  ) {
    return "fr";
  }

  if (
    name.includes("deutsch") ||
    name.includes("german") ||
    name === "de" ||
    name.startsWith("de-") ||
    ["DE", "AT", "CH"].includes(flagUp)
  ) {
    return "de";
  }

  if (
    name.includes("italiano") ||
    name.includes("italian") ||
    name === "it" ||
    name.startsWith("it-") ||
    flagUp === "IT"
  ) {
    return "it";
  }

  if (
    name.includes("chinese") ||
    name.includes("中文") ||
    name === "zh" ||
    name.startsWith("zh-") ||
    ["CN", "TW", "HK"].includes(flagUp)
  ) {
    return "zh";
  }

  return "en";
};

const PRIVACY_I18N: Record<PrivacyLocale, PrivacyTranslation> = {
  en: {
    title: "Privacy Notice",
    close: "CLOSE",
    content: `
      <h1>Privacy Notice</h1>
      <p>In compliance with the General Law on the Protection of Personal Data Held by Obligated Subjects for the State of Quintana Roo, the company authorized by SATQ to process your VISITAX payment, in its capacity as an Obligated Subject, informs you that the personal data collected from you will be used mainly to generate your payment on the website http://visitaxonline.com, as well as to prepare reports and statistics and, when necessary, establish communication to respond to your requests in a timely manner and clarify questions about your data, whether due to error or inaccuracy.</p>
      <p>We assume the obligation to comply with sufficient legal and security measures to protect the personal data collected, which will be protected in accordance with the provisions of the aforementioned law and other applicable regulations.</p>
      <p>To exercise ARCO rights, you may contact the Transparency Unit located at Tacubaya, Miguel Hidalgo, 11870 Mexico City, CDMX. For more information about the use of your personal data, you may send an email to transparencyunit@visitaxonline.com.</p>
    `,
  },
  es: {
    title: "Aviso de privacidad",
    close: "CERRAR",
    content: `
      <h1>Aviso de privacidad</h1>
      <p>En cumplimiento con la Ley General de Protección de Datos Personales en Posesión de los Sujetos Obligados del Estado de Quintana Roo, la empresa autorizada por el SATQ para realizar su pago de VISITAX, en su calidad de Sujeto Obligado, le informa que los datos personales que recabamos de usted se utilizarán principalmente para generar su pago en el sitio web http://visitaxonline.com, así como para la elaboración de informes y estadísticas y, en su caso, establecer comunicación para atender sus solicitudes de manera oportuna y aclarar dudas sobre sus datos, ya sea por error o inexactitud.</p>
      <p>Asumimos la obligación de cumplir con las medidas legales y de seguridad suficientes para proteger los datos personales recabados, los cuales serán protegidos de acuerdo con lo dispuesto en la ley mencionada y demás normatividad aplicable.</p>
      <p>Para el ejercicio de los derechos ARCO, puede acudir a la Unidad de Transparencia, ubicada en Tacubaya, Miguel Hidalgo, 11870 Ciudad de México, CDMX. Para más información sobre el uso de sus datos personales, puede enviar un correo electrónico a transparencyunit@visitaxonline.com.</p>
    `,
  },
  pt: {
    title: "Aviso de privacidade",
    close: "FECHAR",
    content: `
      <h1>Aviso de privacidade</h1>
      <p>Em cumprimento à Lei Geral de Proteção de Dados Pessoais em Posse dos Sujeitos Obrigados do Estado de Quintana Roo, a empresa autorizada pelo SATQ para processar seu pagamento VISITAX, na qualidade de Sujeito Obrigado, informa que os dados pessoais coletados de você serão utilizados principalmente para gerar seu pagamento no site http://visitaxonline.com, bem como para elaborar relatórios e estatísticas e, quando necessário, estabelecer comunicação para atender suas solicitações em tempo hábil e esclarecer dúvidas sobre seus dados, seja por erro ou inexatidão.</p>
      <p>Assumimos a obrigação de cumprir medidas legais e de segurança suficientes para proteger os dados pessoais coletados, que serão protegidos de acordo com a lei mencionada e demais normas aplicáveis.</p>
      <p>Para exercer os direitos ARCO, você pode entrar em contato com a Unidade de Transparência localizada em Tacubaya, Miguel Hidalgo, 11870 Cidade do México, CDMX. Para mais informações sobre o uso de seus dados pessoais, envie um e-mail para transparencyunit@visitaxonline.com.</p>
    `,
  },
  fr: {
    title: "Avis de confidentialité",
    close: "FERMER",
    content: `
      <h1>Avis de confidentialité</h1>
      <p>Conformément à la Loi générale sur la protection des données personnelles détenues par les sujets obligés de l'État de Quintana Roo, la société autorisée par le SATQ à traiter votre paiement VISITAX, en sa qualité de sujet obligé, vous informe que les données personnelles collectées auprès de vous seront principalement utilisées pour générer votre paiement sur le site http://visitaxonline.com, ainsi que pour préparer des rapports et des statistiques et, le cas échéant, établir une communication afin de répondre à vos demandes en temps opportun et de clarifier toute question concernant vos données, qu'elle résulte d'une erreur ou d'une inexactitude.</p>
      <p>Nous assumons l'obligation de respecter des mesures légales et de sécurité suffisantes pour protéger les données personnelles collectées, lesquelles seront protégées conformément à la loi susmentionnée et aux autres réglementations applicables.</p>
      <p>Pour exercer vos droits ARCO, vous pouvez contacter l'Unité de transparence située à Tacubaya, Miguel Hidalgo, 11870 Mexico, CDMX. Pour plus d'informations sur l'utilisation de vos données personnelles, vous pouvez envoyer un e-mail à transparencyunit@visitaxonline.com.</p>
    `,
  },
  de: {
    title: "Datenschutzhinweis",
    close: "SCHLIESSEN",
    content: `
      <h1>Datenschutzhinweis</h1>
      <p>In Übereinstimmung mit dem Allgemeinen Gesetz zum Schutz personenbezogener Daten im Besitz verpflichteter Stellen des Bundesstaates Quintana Roo informiert Sie das vom SATQ autorisierte Unternehmen, das Ihre VISITAX-Zahlung verarbeitet, in seiner Eigenschaft als verpflichtete Stelle darüber, dass die von Ihnen erhobenen personenbezogenen Daten hauptsächlich verwendet werden, um Ihre Zahlung auf der Website http://visitaxonline.com zu generieren, sowie zur Erstellung von Berichten und Statistiken und, falls erforderlich, zur Kommunikation, um Ihre Anfragen rechtzeitig zu bearbeiten und Fragen zu Ihren Daten zu klären, sei es aufgrund eines Fehlers oder einer Ungenauigkeit.</p>
      <p>Wir verpflichten uns, ausreichende rechtliche und sicherheitstechnische Maßnahmen zum Schutz der erhobenen personenbezogenen Daten einzuhalten. Diese Daten werden gemäß dem genannten Gesetz und den übrigen anwendbaren Vorschriften geschützt.</p>
      <p>Zur Ausübung der ARCO-Rechte können Sie sich an die Transparenzeinheit wenden, die sich in Tacubaya, Miguel Hidalgo, 11870 Mexiko-Stadt, CDMX befindet. Weitere Informationen zur Verwendung Ihrer personenbezogenen Daten erhalten Sie per E-Mail an transparencyunit@visitaxonline.com.</p>
    `,
  },
  it: {
    title: "Informativa sulla privacy",
    close: "CHIUDI",
    content: `
      <h1>Informativa sulla privacy</h1>
      <p>In conformità con la Legge generale sulla protezione dei dati personali in possesso dei soggetti obbligati dello Stato di Quintana Roo, la società autorizzata dal SATQ a processare il pagamento VISITAX, nella sua qualità di Soggetto Obbligato, informa che i dati personali raccolti saranno utilizzati principalmente per generare il pagamento sul sito web http://visitaxonline.com, nonché per preparare report e statistiche e, se necessario, stabilire comunicazioni per rispondere tempestivamente alle richieste e chiarire dubbi sui dati, sia per errore sia per inesattezza.</p>
      <p>Assumiamo l'obbligo di rispettare misure legali e di sicurezza sufficienti per proteggere i dati personali raccolti, che saranno tutelati in conformità con la legge sopra citata e le altre normative applicabili.</p>
      <p>Per esercitare i diritti ARCO, è possibile rivolgersi all'Unità di Trasparenza situata in Tacubaya, Miguel Hidalgo, 11870 Città del Messico, CDMX. Per maggiori informazioni sull'uso dei dati personali, è possibile inviare un'e-mail a transparencyunit@visitaxonline.com.</p>
    `,
  },
  zh: {
    title: "隐私声明",
    close: "关闭",
    content: `
      <h1>隐私声明</h1>
      <p>根据金塔纳罗奥州关于义务主体持有个人数据保护的普通法，经 SATQ 授权处理您 VISITAX 付款的公司，作为义务主体，特此告知您：我们向您收集的个人数据将主要用于在 http://visitaxonline.com 网站生成您的付款，并用于制作报告和统计信息；必要时，也会用于与您沟通，以便及时处理您的请求，并澄清因错误或不准确而产生的有关您数据的问题。</p>
      <p>我们承诺遵守充分的法律和安全措施，以保护已收集的个人数据。该等数据将依照上述法律及其他适用法规予以保护。</p>
      <p>如需行使 ARCO 权利，您可以联系位于 Tacubaya, Miguel Hidalgo, 11870 Mexico City, CDMX 的透明度部门。有关个人数据使用的更多信息，您可以发送电子邮件至 transparencyunit@visitaxonline.com。</p>
    `,
  },
};

const ModalTextPrivacity = ({
  open = false,
  onClose = () => {},
}: ModalTextPrivacityProps) => {
  const configurations = useConfigurations();
  const currentLanguageName =
    configurations.lenguageList?.[configurations.currentlenguage];
  const currentFlag = configurations.flags?.[configurations.currentlenguage];
  const locale = resolvePrivacyLocale(currentLanguageName, currentFlag);
  const translation = PRIVACY_I18N[locale] ?? PRIVACY_I18N.en;

  const handleClose = () => {
    onClose();
  };

  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className="customized-dialog-title">
          {translation.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Typography
            component="div"
            gutterBottom
            style={{ textAlign: "justify" }}
            dangerouslySetInnerHTML={{ __html: translation.content }}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            {translation.close}
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </React.Fragment>
  );
};

export default ModalTextPrivacity;
